import React, { useEffect } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";

// Toast types and interfaces
export interface Toast {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

// Simple toast store
class ToastStore {
  private toasts: Toast[] = [];
  private listeners: ((toasts: Toast[]) => void)[] = [];

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.push(listener);
    listener(this.toasts);

    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  getSnapshot() {
    return this.toasts;
  }

  addToast(toast: Omit<Toast, "id">) {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = { ...toast, id };

    this.toasts.push(newToast);
    this.notify();

    return id;
  }

  removeToast(id: string) {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.notify();
  }

  removeAllToasts() {
    this.toasts = [];
    this.notify();
  }

  private notify() {
    this.listeners.forEach((listener) => listener(this.toasts));
  }
}

const toastStore = new ToastStore();

// Toast hook
export const useToast = () => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  React.useEffect(() => {
    return toastStore.subscribe(setToasts);
  }, []);

  const toast = React.useCallback((options: Omit<Toast, "id">) => {
    return toastStore.addToast(options);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    toastStore.removeToast(id);
  }, []);

  const dismissAll = React.useCallback(() => {
    toastStore.removeAllToasts();
  }, []);

  return {
    toasts,
    toast,
    dismiss,
    dismissAll,
  };
};

// Individual toast component
const ToastItem: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-neon-lime" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-neon-pink-soft" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-warning-400" />;
      case "info":
        return <Info className="h-5 w-5 text-neon-cyan" />;
      default:
        return <Info className="h-5 w-5 text-fg-muted" />;
    }
  };

  const getBorderColor = () => {
    switch (toast.type) {
      case "success":
        return "border-l-neon-lime";
      case "error":
        return "border-l-neon-pink";
      case "warning":
        return "border-l-warning-500";
      case "info":
        return "border-l-neon-cyan";
      default:
        return "border-l-white/[.25]";
    }
  };

  useEffect(() => {
    if (toast.duration === 0) return; // Don't auto-remove if duration is 0

    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, toast.duration || 8000); // 8 seconds for better readability

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  return (
    <div
      className={`
      w-full bg-surface border border-white/[.09] shadow-panel rounded-card pointer-events-auto
      border-l-4 ${getBorderColor()}
      transform transition-all duration-500 ease-out
      animate-toast-enter
    `}
    >
      <div className="p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0">{getIcon()}</div>
          <div className="ml-4 flex-1 min-w-0">
            <p className="text-base font-semibold text-fg-bright leading-tight">{toast.title}</p>
            {toast.description && (
              <p className="mt-2 text-sm text-fg-body leading-relaxed">{toast.description}</p>
            )}
            {toast.action && (
              <div className="mt-3">
                <button
                  onClick={toast.action.onClick}
                  className="text-sm sf-link focus:outline-none focus:underline"
                >
                  {toast.action.label}
                </button>
              </div>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={() => onRemove(toast.id)}
              className="rounded-lg inline-flex text-fg-muted hover:text-fg-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-pink/50 transition-colors duration-200 p-1"
            >
              <span className="sr-only">Sluiten</span>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Toaster component
export const Toaster: React.FC = () => {
  const { toasts, dismiss, dismissAll } = useToast();

  // Clear all toasts on page reload/unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      dismissAll();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Also clear on component mount (page reload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dismissAll]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-[60] space-y-3 pointer-events-none w-96 max-w-[calc(100vw-2rem)]">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={dismiss} />
      ))}
    </div>
  );
};

// Export toast function for easy use
export const toast = {
  success: (title: string, description?: string, options?: Partial<Toast>) => {
    const toastData = { type: "success" as const, title, ...options };
    if (description) toastData.description = description;
    return toastStore.addToast(toastData);
  },

  error: (title: string, description?: string, options?: Partial<Toast>) => {
    const toastData = { type: "error" as const, title, ...options };
    if (description) toastData.description = description;
    return toastStore.addToast(toastData);
  },

  warning: (title: string, description?: string, options?: Partial<Toast>) => {
    const toastData = { type: "warning" as const, title, ...options };
    if (description) toastData.description = description;
    return toastStore.addToast(toastData);
  },

  info: (title: string, description?: string, options?: Partial<Toast>) => {
    const toastData = { type: "info" as const, title, ...options };
    if (description) toastData.description = description;
    return toastStore.addToast(toastData);
  },
};

export default Toaster;
